import { useState } from "react";
import { motion } from "framer-motion";
import CTABanner from "../components/sections/CTABanner";
import useSEO from "../lib/useSEO";

const FLEET = [
    {
        id: "wms-aurora",
        name: "MV Windermere Aurora",
        category: "Bulk Carrier",
        image: "https://images.unsplash.com/photo-1673170705689-abc04a056a88?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { DWT: "82,500 MT", LOA: "229 m", Beam: "32.2 m", Draft: "14.5 m", Year: "2019", Flag: "Singapore", Class: "DNV" },
    },
    {
        id: "wms-meridian",
        name: "MT Windermere Meridian",
        category: "Product Tanker",
        image: "https://images.unsplash.com/photo-1746481594820-04358d2055cd?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { DWT: "49,800 MT", LOA: "183 m", Beam: "32.2 m", Draft: "12.2 m", Year: "2021", Flag: "Singapore", Class: "ABS" },
    },
    {
        id: "wms-orion",
        name: "OSV Windermere Orion",
        category: "Offshore Support",
        image: "https://images.unsplash.com/photo-1595936398510-7057e9288156?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { BHP: "8,200", LOA: "75 m", Beam: "18 m", Draft: "6.4 m", "Bollard Pull": "120 T", Year: "2020", Flag: "Singapore" },
    },
    {
        id: "wms-northstar",
        name: "MV Windermere Northstar",
        category: "Container Vessel",
        image: "https://images.unsplash.com/photo-1713859288954-3314e2fcaa7a?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { TEU: "4,300", LOA: "260 m", Beam: "32.2 m", Draft: "13 m", Year: "2022", Flag: "Singapore", Class: "Lloyds" },
    },
    {
        id: "wms-polaris",
        name: "MT Windermere Polaris",
        category: "Crude Tanker",
        image: "https://images.unsplash.com/photo-1746481594820-04358d2055cd?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { DWT: "115,000 MT", LOA: "248 m", Beam: "44 m", Draft: "15.4 m", Year: "2018", Flag: "Singapore", Class: "DNV" },
    },
    {
        id: "wms-cassiopeia",
        name: "MV Windermere Cassiopeia",
        category: "Bulk Carrier",
        image: "https://images.unsplash.com/photo-1673170705689-abc04a056a88?crop=entropy&cs=srgb&fm=jpg&q=85",
        specs: { DWT: "63,500 MT", LOA: "200 m", Beam: "32.2 m", Draft: "13.3 m", Year: "2020", Flag: "Singapore", Class: "ABS" },
    },
];

const FILTERS = ["All", "Bulk Carrier", "Product Tanker", "Crude Tanker", "Offshore Support", "Container Vessel"];

export default function Fleet() {
    useSEO({
        title: "Fleet — Windermere Shipping",
        description: "A modern fleet of bulk carriers, tankers, offshore support and container vessels operated from Singapore.",
    });
    const [filter, setFilter] = useState("All");
    const [active, setActive] = useState(FLEET[0]);

    const filtered = filter === "All" ? FLEET : FLEET.filter((v) => v.category === filter);

    return (
        <>
            <section className="relative pt-36 md:pt-44 pb-16 bg-[#040914] border-b border-white/5">
                <div className="wms-container">
                    <span className="wms-overline">Fleet — Tonnage</span>
                    <h1 className="mt-6 font-display font-light text-[clamp(2.4rem,6vw,5rem)] leading-[1.05] tracking-tight">
                        Modern. Multi-segment. <span className="italic text-[#48CAE4]">Classed worldwide.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
                        A purpose-built fleet across bulk, tanker, offshore and container segments — managed in-house with full IMO compliance.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-24 bg-[#040914]">
                <div className="wms-container">
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="fleet-filters">
                        {FILTERS.map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`fleet-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                                    filter === f
                                        ? "border-[#48CAE4] text-white bg-[#48CAE4]/10"
                                        : "border-white/10 text-white/55 hover:text-white hover:border-white/30"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>

                    {/* Active vessel hero */}
                    <motion.div
                        key={active.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5 border border-white/5 mb-10"
                    >
                        <div className="lg:col-span-7 bg-[#040914] relative aspect-[16/10] overflow-hidden">
                            <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-transparent to-transparent" />
                            <span className="absolute top-5 left-5 wms-glass px-3 py-1 text-[10px] tracking-[0.32em] uppercase">{active.category}</span>
                        </div>
                        <div className="lg:col-span-5 bg-[#0B1324] p-8 md:p-10 flex flex-col">
                            <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">IMO · WMS-{active.id.slice(-3).toUpperCase()}</span>
                            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">{active.name}</h2>
                            <dl className="mt-8 grid grid-cols-2 gap-px bg-white/5 border border-white/5">
                                {Object.entries(active.specs).map(([k, v]) => (
                                    <div key={k} className="bg-[#0B1324] p-4">
                                        <dt className="text-[10px] tracking-[0.28em] uppercase text-white/40">{k}</dt>
                                        <dd className="mt-2 font-display text-base text-white">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </motion.div>

                    {/* Grid of vessels */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5" data-testid="fleet-grid">
                        {filtered.map((v) => (
                            <button
                                key={v.id}
                                onClick={() => setActive(v)}
                                data-testid={`fleet-tile-${v.id}`}
                                className={`text-left group bg-[#040914] hover:bg-[#0B1324] transition-colors ${
                                    active.id === v.id ? "ring-1 ring-[#48CAE4]" : ""
                                }`}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img src={v.image} alt={v.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-[#040914]/30 to-transparent" />
                                    <span className="absolute top-3 left-3 wms-glass px-2 py-0.5 text-[9px] tracking-[0.28em] uppercase">{v.category}</span>
                                </div>
                                <div className="p-5">
                                    <div className="font-display text-lg text-white">{v.name}</div>
                                    <div className="text-xs text-white/45 mt-1">{Object.values(v.specs).slice(0, 3).join(" · ")}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <CTABanner />
        </>
    );
}
